import { Ref, computed, ref, watch } from '@vue/composition-api';
import smh from '@/renderer/scripts/smh';
import user from '@/renderer/scripts/utils/user';
import storage from '@/renderer/scripts/utils/storage';
import store from '@/renderer/store';
import tips from '@/renderer/scripts/utils/tips';
import { IS_BROWSER } from '@/common/constant/platform';

import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import dispatchWebview from '@/renderer/scripts/utils/tools/dispatchWebview';
import { encodeText } from '@/renderer/scripts/utils/tools';

export default function useSSOLogin() {
  // 企业id下拉列表最多存5项
  const listLimit = 5;
  const corpId = ref('');
  const corpIdList: Ref<string[]> = ref([]);
  const loading = ref(false);
  const remoteCropList = computed(() => store.getters.corpList);

  const methods = {
    // 初始化查询缓存的企业id
    initList(params?: { noCache: boolean }) {
      const noCache = params?.noCache ?? false;
      if (remoteCropList.value.length > 0) {
        corpId.value = remoteCropList.value[0].corpId;
      } else if (!noCache) {
        const storeCorpIdList = storage.get<string[]>(storage.StoreKey.NEW_SSO_CORPID_LIST) || [];
        corpIdList.value = storeCorpIdList.filter((item: string) => typeof item === 'string');
        // 默认选中最新一条企业id
        if (corpIdList.value.length > 0) {
          [corpId.value] = corpIdList.value;
        }
      }
    },
    // 确认时缓存企业id
    addCustomIdList() {
      const list = corpIdList.value;
      const storeKey = storage.StoreKey.NEW_SSO_CORPID_LIST;
      if (!list.includes(corpId.value)) {
        list.unshift(corpId.value);
      }
      if (list.length > listLimit) {
        list.pop();
      }
      storage.set(storeKey, list);
    },
    /**
     * @description
     * @param {{ domain?: string; path?: string }} [options={}]
     *   path: sso重定向回来后的路径，不传则默认重定向前的路径
     * @return {*}
     */
    async openSSOUrl(options: { domain?: string; path?: string } = {}) {
      const { domain, path } = options;
      let isSuccess = false;
      try {
        loading.value = true;
        let finalPath = '';
        // 对path进行先加密再2次encodeURI
        if (IS_BROWSER) {
          finalPath = path ?? `${window.location.pathname}${window.location.search}`;
        }
        if (finalPath) {
          finalPath = encodeURIComponent(encodeURIComponent(encodeText(finalPath)));
        }
        const type = domain ? 'domain' : 'corpId';
        const params = domain
          ? { domain, from: IS_BROWSER ? 'web' : 'pc', custom_state: finalPath }
          : { from: IS_BROWSER ? 'web' : 'pc', custom_state: finalPath, orgDomain: encodeURIComponent(corpId.value) };
        const { data } = await smh.api.getSSOurl(params);
        if (data.url) {
          // 记录是web还是pc打开 pc端打开sso url登录后需要唤起桌面应用
          user.setSSOFrom(IS_BROWSER ? 'web' : 'pc');
          // 记录企业id
          if (type === 'corpId') {
            user.setCorpid(corpId.value);
            methods.addCustomIdList();
          }
          if (IS_BROWSER) {
            // url不包含state 字段，认为是 cas 协议登录，需要存本地信息
            if (!data.url.includes('state=')) {
              storage.set(storage.StoreKey.CAS_LOGIN, {
                path: finalPath,
                time: Date.now(),
              });
            }
            dispatchRouter(data.url, '_self');
          } else {
            dispatchWebview({ url: data.url });
          }
          isSuccess = true;
        }
      } catch (e: any) {
        tips.error(e);
      }
      loading.value = false;
      return isSuccess;
    },
  };

  // 企业id不支持空格
  watch(corpId, val => {
    corpId.value = `${val}`.replace(/\s/g, '').replace(/\./g, '');
  });

  return {
    remoteCropList,
    corpId,
    corpIdList,
    loading,
    methods,
  };
}
